import React, { useState } from 'react';
import { Users, HardHat, Plus, Edit3, Save, Trash2, Phone, Mail, MapPin, Search } from 'lucide-react';
import { useQuery } from '@/core/db-adapter/useQuery';
import { contactsApi } from '@/core/adapter-api';
import { Modal, DetailPanel, StatusBadge, FormField, StatsGrid, EmptyState } from './shared';

interface TeamMember {
  id: string;
  name: string;
  role: string;
  project: string;
  status: string;
  phone: string;
  email: string;
  hourlyRate: number;
  type: 'trabajador' | 'subcontrata';
}

export const EquipoPage: React.FC = () => {
  const rawContacts = useQuery(() => contactsApi.all(), [], ['contacts']) || [];
  
  const team: TeamMember[] = rawContacts
    .filter((c: any) => c.isWorker)
    .map((c: any) => ({
      id: c.id,
      name: c.name,
      role: c.obraRole || 'Rol no definido',
      project: c.notes || 'Múltiples obras',
      status: 'Activo',
      phone: c.phone || '',
      email: c.email || '',
      hourlyRate: c.hourlyRate || 0,
      type: c.type === 'supplier' ? 'subcontrata' : 'trabajador',
    }));

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState<TeamMember | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newMember, setNewMember] = useState({ name: '', role: '', project: '', phone: '', email: '', type: 'trabajador' as 'trabajador' | 'subcontrata', hourlyRate: '' });
  const [searchQuery, setSearchQuery] = useState('');

  const selected = team.find(m => m.id === selectedId);

  const startEdit = () => {
    if (selected) { setEditData({ ...selected }); setEditing(true); }
  };

  const saveEdit = async () => {
    if (editData) {
      await contactsApi.update(editData.id, {
        name: editData.name,
        phone: editData.phone,
        email: editData.email,
        notes: editData.project,
        obraRole: editData.role,
        hourlyRate: editData.hourlyRate as any,
        type: editData.type === 'subcontrata' ? 'supplier' : 'client'
      } as any);
      setEditing(false);
      setEditData(null);
    }
  };

  const deleteMember = async (id: string) => {
    if (!confirm('¿Eliminar este miembro del equipo? Esta acción es irreversible.')) return;
    await contactsApi.delete(id);
    setSelectedId(null);
  };

  const addMember = async () => {
    if (!newMember.name.trim()) return;
    await contactsApi.add({
      name: newMember.name,
      phone: newMember.phone,
      email: newMember.email,
      notes: newMember.project,
      obraRole: newMember.role,
      isWorker: true,
      hourlyRate: parseFloat(newMember.hourlyRate) || 0,
      type: newMember.type === 'subcontrata' ? 'supplier' : 'client'
    } as any);
    setNewMember({ name: '', role: '', project: '', phone: '', email: '', type: 'trabajador', hourlyRate: '' });
    setShowAddModal(false);
  };

  const workers = team.filter(m => m.type === 'trabajador');
  const subcontratas = team.filter(m => m.type === 'subcontrata');
  const active = team.filter(m => m.status === 'Activo').length;
  const onLeave = team.filter(m => m.status === 'De baja').length;
  const filteredTeam = searchQuery
    ? team.filter(m => m.name.toLowerCase().includes(searchQuery.toLowerCase()) || m.role.toLowerCase().includes(searchQuery.toLowerCase()))
    : team;

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-amber-500/15 rounded-xl">
            <Users size={20} className="text-amber-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-100">Equipo</h1>
            <p className="text-sm text-gray-500">Gestión de trabajadores y subcontratas</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
            <input
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Buscar..."
              className="pl-9 pr-3 py-2 bg-[#0d1117] border border-white/10 rounded-xl text-sm text-gray-200 placeholder:text-gray-600 focus:border-amber-500/50 focus:outline-none w-40"
            />
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-xl text-sm font-semibold hover:bg-amber-700 transition-colors"
          >
            <Plus size={16} /> Añadir
          </button>
        </div>
      </div>

      <StatsGrid stats={[
        { label: 'Trabajadores', value: String(workers.length) },
        { label: 'Subcontratas', value: String(subcontratas.length) },
        { label: 'En obra hoy', value: String(active), color: 'text-emerald-400' },
        { label: 'De baja', value: String(onLeave), color: 'text-red-400' },
      ]} />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredTeam.length === 0 && (
          <div className="col-span-full">
            <EmptyState
              icon={Users}
              title="Sin miembros de equipo"
              description="Añade trabajadores y subcontratas para gestionar tu equipo de obra."
              action={{ label: 'Añadir miembro', onClick: () => setShowAddModal(true) }}
            />
          </div>
        )}
        {filteredTeam.map((member) => {
          const initials = member.name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();
          const colors = ['from-amber-500 to-orange-600', 'from-blue-500 to-indigo-600', 'from-emerald-500 to-teal-600', 'from-purple-500 to-pink-600', 'from-rose-500 to-red-600'];
          const colorIdx = member.name.charCodeAt(0) % colors.length;
          return (
            <button
              key={member.id}
              onClick={() => setSelectedId(selectedId === member.id ? null : member.id)}
              className={`group relative bg-[#0d1117] border rounded-xl p-5 text-left transition-all hover:-translate-y-0.5 ${
                selectedId === member.id ? 'border-amber-500/30 bg-amber-500/5' : 'border-white/8 hover:border-white/15'
              }`}
            >
              <div className="flex items-center gap-3 mb-3">
                <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${colors[colorIdx]} flex items-center justify-center text-white text-sm font-bold shadow-lg`}>
                  {initials}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-gray-100 truncate">{member.name}</p>
                  <p className="text-xs text-gray-500 truncate">{member.role}</p>
                </div>
                <StatusBadge status={member.status} />
              </div>
              <div className="space-y-1.5">
                {member.phone && (
                  <div className="flex items-center gap-2 text-xs text-gray-400">
                    <Phone size={11} className="text-gray-600" /> {member.phone}
                  </div>
                )}
                {member.email && (
                  <div className="flex items-center gap-2 text-xs text-gray-400 truncate">
                    <Mail size={11} className="text-gray-600" /> {member.email}
                  </div>
                )}
              </div>
              <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/5">
                <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${
                  member.type === 'subcontrata' ? 'bg-purple-500/10 text-purple-400' : 'bg-blue-500/10 text-blue-400'
                }`}>
                  {member.type === 'subcontrata' ? 'Subcontrata' : 'Trabajador'}
                </span>
                {member.type === 'trabajador' && member.hourlyRate > 0 && (
                  <span className="text-sm font-bold text-amber-400">{member.hourlyRate}€/h</span>
                )}
              </div>
            </button>
          );
        })}
      </div>

      <DetailPanel open={!!selected && !editing} onClose={() => setSelectedId(null)}>
        {selected && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-lg font-bold text-gray-100">{selected.name}</h4>
              <div className="flex gap-2">
                <button onClick={startEdit} className="p-2 text-gray-400 hover:text-amber-400 transition-colors" title="Editar">
                  <Edit3 size={16} />
                </button>
                <button onClick={() => deleteMember(selected.id)} className="p-2 text-gray-400 hover:text-red-400 transition-colors" title="Eliminar">
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex items-center gap-2 text-sm text-gray-300">
                <HardHat size={14} className="text-amber-500" /> {selected.role}
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-300">
                <MapPin size={14} className="text-amber-500" /> {selected.project}
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-300">
                <Phone size={14} className="text-amber-500" /> {selected.phone}
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-300">
                <Mail size={14} className="text-amber-500" /> {selected.email}
              </div>
            </div>
            {selected.type === 'trabajador' && (
              <div className="mt-4 p-3 bg-[#0d1117] rounded-lg">
                <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Tarifa por hora</p>
                <p className="text-xl font-bold text-amber-400 mt-1">{selected.hourlyRate}€/h</p>
              </div>
            )}
          </div>
        )}
      </DetailPanel>

      <Modal open={editing} onClose={() => setEditing(false)} title="Editar miembro">
        {editData && (
          <div className="space-y-4">
            <FormField label="Nombre" value={editData.name} onChange={(v) => setEditData({ ...editData, name: v })} />
            <FormField label="Rol" value={editData.role} onChange={(v) => setEditData({ ...editData, role: v })} />
            <FormField label="Proyecto" value={editData.project} onChange={(v) => setEditData({ ...editData, project: v })} />
            <FormField label="Teléfono" value={editData.phone} onChange={(v) => setEditData({ ...editData, phone: v })} />
            <FormField label="Email" value={editData.email} onChange={(v) => setEditData({ ...editData, email: v })} />
            <FormField label="Tarifa (€/h)" value={String(editData.hourlyRate)} onChange={(v) => setEditData({ ...editData, hourlyRate: parseFloat(v) || 0 })} type="number" />
            <button
              onClick={saveEdit}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-amber-600 text-white rounded-xl font-semibold hover:bg-amber-700 transition-colors"
            >
              <Save size={16} /> Guardar cambios
            </button>
          </div>
        )}
      </Modal>

      <Modal open={showAddModal} onClose={() => setShowAddModal(false)} title="Añadir miembro">
        <div className="space-y-4">
          <FormField label="Nombre" value={newMember.name} onChange={(v) => setNewMember({ ...newMember, name: v })} />
          <FormField label="Rol" value={newMember.role} onChange={(v) => setNewMember({ ...newMember, role: v })} placeholder="Ej: Electricista, Albañil..." />
          <FormField label="Proyecto" value={newMember.project} onChange={(v) => setNewMember({ ...newMember, project: v })} />
          <FormField label="Teléfono" value={newMember.phone} onChange={(v) => setNewMember({ ...newMember, phone: v })} />
          <FormField label="Email" value={newMember.email} onChange={(v) => setNewMember({ ...newMember, email: v })} />
          <FormField label="Tarifa (€/h)" value={newMember.hourlyRate} onChange={(v) => setNewMember({ ...newMember, hourlyRate: v })} type="number" placeholder="Ej: 18" />
          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Tipo</label>
            <select
              value={newMember.type}
              onChange={(e) => setNewMember({ ...newMember, type: e.target.value as 'trabajador' | 'subcontrata' })}
              className="w-full bg-[#0d1117] border border-white/10 rounded-lg px-3 py-2 text-sm text-gray-200 focus:border-amber-500/50 focus:outline-none"
            >
              <option value="trabajador">Trabajador</option>
              <option value="subcontrata">Subcontrata</option>
            </select>
          </div>
          <button
            onClick={addMember}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-amber-600 text-white rounded-xl font-semibold hover:bg-amber-700 transition-colors"
          >
            <Plus size={16} /> Añadir miembro
          </button>
        </div>
      </Modal>
    </div>
  );
};
